
import { useState } from "react";
import { Heart, MessageCircle, MapPin, Radio, Clock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { CastResponseModal } from "./CastResponseModal";
import { ReportButton } from "./ReportButton";
import { TagButton } from "./TagButton";
import { FeedOptimizedAvatar } from "./FeedOptimizedAvatar";
import { formatBroadcastRadius } from "@/utils/distanceUtils";
import { formatLocationForDisplay } from "@/utils/locationUtils";
import { formatRelativeSafe } from "@/utils/date";
import type { Cast } from "@/hooks/useCasts";

interface CastCardProps {
  cast: Cast & {
    solved?: boolean;
  };
  onLike: (castId: string) => void;
  onComment: (castId: string) => void;
}

export const CastCard = ({ cast, onLike, onComment }: CastCardProps) => {
  const [isLiked, setIsLiked] = useState(cast.isLiked || false);
  const [showResponseModal, setShowResponseModal] = useState(false);

  const handleLike = () => {
    setIsLiked(!isLiked);
    onLike(cast.id);
  };

  const handleComment = () => {
    setShowResponseModal(true);
    onComment(cast.id);
  };

  const getPriorityColor = (sentiment?: string) => {
    switch (sentiment) {
      case "help":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "px-2 py-0.5 text-xs rounded-full border-iso-soft-border bg-iso-soft-bg text-[#3E1C8A]";
    }
  };

  const getPriorityLabel = (sentiment?: string) => {
    switch (sentiment) {
      case "help":
        return "Urgent";
      default:
        return "General";
    }
  };

  return (
    <>
      <div className={`rounded-lg shadow-sm border overflow-hidden ${
        cast.solved 
          ? 'bg-gray-50 border-gray-200' 
          : cast.sentiment === 'help' 
            ? 'bg-red-50 border-red-200' 
            : 'bg-white border-gray-200'
      }`}>
        {/* Compact Cast Header */}
        <div className={`px-3 py-1.5 border-b ${
          cast.solved 
            ? 'bg-gray-100 border-gray-200' 
            : cast.sentiment === 'help'
              ? 'bg-red-100 border-red-200'
              : 'bg-white/5 border-white/10'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className={
                cast.solved 
                  ? "text-gray-600" 
                  : cast.sentiment === 'help' 
                    ? "text-red-600" 
                    : "text-iso-solid"
              } size={12} />
              <span className={`text-xs font-medium ${
                cast.solved 
                  ? "text-gray-700" 
                  : cast.sentiment === 'help' 
                    ? "text-red-800" 
                    : "text-[#3E1C8A]"
              }`}>
                ISO Cast
              </span>
              {cast.solved && (
                <Badge className="bg-gray-200 text-gray-700 border-gray-300 text-xs px-1 py-0">
                  <CheckCircle size={8} className="mr-0.5" />
                  Solved
                </Badge>
              )}
              {!cast.solved && (
                <Badge className={`text-xs px-1 py-0 ${getPriorityColor(cast.sentiment)}`}>
                  {getPriorityLabel(cast.sentiment)}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1 text-xs ${
                cast.solved 
                  ? "text-gray-600" 
                  : cast.sentiment === 'help' 
                    ? "text-red-600" 
                    : "text-iso-solid"
              }`}>
                <Clock size={8} />
                <span>
                  {formatRelativeSafe(cast.created_at as any)}
                </span>
              </div>
              <ReportButton
                contentId={cast.id}
                contentType="cast"
                contentPreview={cast.iso_request}
              />
            </div>
          </div>
        </div>

        {/* Compact Content */}
        <div className="p-2">
          <div className="flex items-start space-x-2 mb-2">
            <FeedOptimizedAvatar 
              src={cast.user?.avatar}
              alt={cast.user?.name || 'Unknown User'}
              fallback={cast.user?.name?.[0] || 'U'}
              className="w-6 h-6"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-1">
                <h3 className="font-semibold text-gray-900 text-xs truncate">{cast.user?.name || 'Unknown User'}</h3>
                {cast.user?.verified && (
                  <div className="w-2 h-2 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
                <span className="text-gray-500 text-xs truncate">@{cast.user?.username || 'unknown'}</span>
              </div>
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                <MapPin size={8} />
                <span className="truncate">{formatLocationForDisplay(cast.location_address, "Location available")}</span>
                {cast.distanceText && (
                  <>
                    <span>•</span>
                    <span className="flex-shrink-0">{cast.distanceText}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Compact ISO Request */}
          <div className="mb-2">
            <div className={`rounded-md p-2 border-l-2 ${
              cast.solved 
                ? 'bg-gray-100 border-gray-400' 
                : cast.sentiment === 'help'
                  ? 'bg-red-100 border-red-500'
                  : 'bg-white/5 border-orange-500'
            }`}>
              <h4 className="font-medium text-gray-800 text-xs mb-1">Looking for:</h4>
              <p className={`text-gray-700 text-sm ${cast.solved ? 'line-through opacity-75' : ''}`}>
                {cast.iso_request}
              </p>
            </div>
          </div>

          {/* Compact Actions */}
          <div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleLike}
                className={`flex items-center space-x-1 transition-colors ${
                  isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                }`}
              >
                <Heart size={12} className={isLiked ? 'fill-current' : ''} />
                <span className="text-xs">{cast.likes_count}</span>
              </button>
              <button
                onClick={handleComment}
                className="flex items-center space-x-1 text-gray-500 hover:text-blue-500 transition-colors"
              >
                <MessageCircle size={12} />
                <span className="text-xs">{cast.comments_count}</span>
              </button>
              <TagButton
                targetUserId={(cast as any).user_id || ''}
                targetUserName={cast.user?.name || 'Unknown User'}
                contentType="cast"
                contentId={cast.id}
              />
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleComment}
              className={
                cast.solved 
                  ? "bg-white/5 border-gray-200 text-gray-600 hover:bg-white/10 text-xs h-6 px-2"
                  : cast.sentiment === 'help'
                    ? "bg-red-50 border-red-200 text-red-700 hover:bg-red-100 text-xs h-6 px-2"
                    : "bg-orange-500/10 border-orange-200 text-orange-700 hover:bg-orange-500/20 text-xs h-6 px-2"
              }
              disabled={cast.solved}
            >
              {cast.solved ? "Solved" : "Respond"}
            </Button>
          </div>
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && (
        <CastResponseModal
          cast={cast}
          onClose={() => setShowResponseModal(false)}
        />
      )}
    </>
  );
};
